import { Fragment, useState } from "react";

import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import {
  computeToolCallStatus,
  ToolCallAccordionItem,
} from "src/base-components/agents/ToolCallDisplay";
import type { ToolCallGroupItem } from "src/base-components/agents/ToolCallGroup";
import { Icon } from "src/design-system/Icon";

export const UnifiedCopilotToolCallGroup: React.FC<{
  items: ToolCallGroupItem[];
  /**
   * Model iterations behind these calls, so the header counts the agent's
   * steps rather than its tool calls: five calls over two iterations read as
   * "2 steps".
   */
  stepCount: number;
  /** Renders the text runs the thread folds in. */
  renderText: (text: string) => React.ReactNode;
  dataLoc?: string;
}> = ({ items, stepCount, renderText, dataLoc }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div data-loc={dataLoc}>
      <button
        className="flex items-center gap-x-1 font-inter-normal-13px text-gray-500"
        type="button"
        onClick={() => setExpanded(!expanded)}
      >
        {`${stepCount} ${stepCount === 1 ? "step" : "steps"}`}
        <div
          className={twJoin(
            "flex h-3 w-3 shrink-0 items-center justify-center transition-transform duration-200",
            expanded ? "rotate-0" : "-rotate-90",
          )}
        >
          <Icon icon={faChevronDown} size="2xs" />
        </div>
      </button>
      {expanded && (
        <div className="mt-1 space-y-2">
          {items.map((item) =>
            item.kind === "text" ? (
              <Fragment key={item.key}>{renderText(item.text)}</Fragment>
            ) : (
              // An open call is left without a status: the turn's own loading
              // indicator is the one status for all agent activity, so a row
              // here must not spin as well.
              <ToolCallAccordionItem
                key={item.toolCall.id}
                showDescription={false}
                showTokenUsage={false}
                status={computeToolCallStatus(item.toolResult, null)}
                toolCall={item.toolCall}
                toolResult={item.toolResult}
              />
            ),
          )}
        </div>
      )}
    </div>
  );
};
